import { CodeSnippetTabs } from './CodeSnippetTabs'
import { ModelDetailSection } from './ModelDetailSection'
import type { GenerationType } from '../../../../../shared-generation-pricing-config'

const API_BASE_URL = 'https://api.modelstack.cc/v1'

interface ModelDetailGenerationCodeExamplesProps {
  baseModel: string
  genType: GenerationType
  supportedParams: Array<string>
}

function exampleModelSettings(supportedParams: Array<string>): Record<string, string> {
  const settings: Record<string, string> = {}
  if (supportedParams.includes('aspect_ratio')) settings.aspect_ratio = '16:9'
  if (supportedParams.includes('duration')) settings.duration = '5'
  if (supportedParams.includes('resolution')) settings.resolution = '1K'
  if (supportedParams.includes('voice')) settings.voice = 'Rachel'
  return settings
}

function indent(json: string, spaces: number) {
  const pad = ' '.repeat(spaces)
  return json.split('\n').map((line, i) => (i === 0 ? line : pad + line)).join('\n')
}

function buildSnippets(baseModel: string, genType: GenerationType, supportedParams: Array<string>) {
  const modelSettings = exampleModelSettings(supportedParams)
  const hasSettings = Object.keys(modelSettings).length > 0

  const body = {
    type: genType,
    prompt: 'A serene mountain landscape at sunset',
    model: baseModel,
    ...(hasSettings ? { modelSettings } : {}),
  }

  return [
    {
      label: 'Python',
      language: 'python',
      code: `import requests

response = requests.post(
    "${API_BASE_URL}/generations",
    headers={"Authorization": "Bearer sk_YOUR_API_KEY"},
    json=${indent(JSON.stringify(body, null, 4), 4)},
)
generation_id = response.json()["data"]["id"]

# Poll for the result
result = requests.get(
    f"${API_BASE_URL}/generations/{generation_id}",
    headers={"Authorization": "Bearer sk_YOUR_API_KEY"},
).json()
print(result["data"]["status"])`,
    },
    {
      label: 'Node.js',
      language: 'javascript',
      code: `const res = await fetch('${API_BASE_URL}/generations', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer sk_YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(${indent(JSON.stringify(body, null, 2), 2)}),
})
const { data } = await res.json()

// Poll for the result
const result = await fetch(\`${API_BASE_URL}/generations/\${data.id}\`, {
  headers: { Authorization: 'Bearer sk_YOUR_API_KEY' },
}).then((r) => r.json())
console.log(result.data.status)`,
    },
    {
      label: 'cURL',
      language: 'bash',
      code: `curl -X POST "${API_BASE_URL}/generations" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${indent(JSON.stringify(body, null, 2), 2)}'`,
    },
  ]
}

export function ModelDetailGenerationCodeExamples({ baseModel, genType, supportedParams }: ModelDetailGenerationCodeExamplesProps) {
  const snippets = buildSnippets(baseModel, genType, supportedParams)

  return (
    <ModelDetailSection title="Code examples" note="POST /v1/generations">
      <p className="mb-3 text-body-sm text-[var(--color-text-tertiary)]">
        Generations are async — the request returns immediately with an id; poll for the result.
      </p>
      <CodeSnippetTabs snippets={snippets} />
    </ModelDetailSection>
  )
}
